import { BannerType } from "fond/hooks/useCanIPlanner";
import { SidebarSection } from "fond/projects/Navigation/types";
import { EntityType, ProjectType } from "fond/types";
import { NewItemSource, StarEventSource } from "fond/types/analytics";

import { boolean, event, looseObject, number, oneOf, string } from "../schema-util";

import { entityId, entityType, noProps, projectId, reportId } from "./shared";

// TODO - be more explicit and only pass on the properties we actually need
const tourEvent = {
  tourKey: string().describe("The key of the tour"),
  action: string().describe("The action taken on the tour"),
  controlled: boolean().describe("Whether the tour is automatic or not"),
  index: number().describe("The index of the current step"),
  lifecycle: string().describe("The lifecycle of the tour. Seems to always be 'init'"),
  origin: string().nullable().describe("The origin of the tour"),
  size: number().describe("The number of steps in the tour"),
  status: string().describe("The status of the tour"),
  type: string().describe("The type of the event"),
};

const newItemSource = oneOf(NewItemSource).describe("Where the new item action was initiated from.");

const starEventSource = oneOf(StarEventSource).describe("Where the star action was initiated from");

export const misc = {
  "workspace-list.folder.created": noProps.describe("User created a new folder"),
  "new-item.folder.clicked": event({ source: newItemSource }).describe("User clicked the New Item > Folder button"),
  "new-item.market-lens.clicked": event({ source: newItemSource }).describe("User clicked the New Item > Market Lens Project button"),
  "New Item.Import Report.Import report menu item clicked": event({ source: newItemSource }).describe(
    "User clicked the New Item > Import Report button"
  ),
  "New Item.Project.Created project": event({ projectId, projectType: oneOf(ProjectType), source: newItemSource }).describe(
    "User created a new project from the New Item button"
  ),
  "New Item.Report.Create report menu item clicked": event({ source: newItemSource }).describe("User clicked the New Item > Report button"),

  "A viral design project is viewed": event({
    projectId,
    projectName: string(),
  }),
  "A viral design report is viewed": event({
    reportId,
    reportName: string(),
  }),
  "Changed password": noProps.describe("User successfully changed their password"),
  "Re-sent email verification code": noProps.describe("UNUSED: (dead code path)"),
  "Verified email": noProps.describe("UNUSED: (dead code path)"),
  "Signed in": event({ source: oneOf(["signin-form", "redux-signin"]) }),
  "Signed out": noProps,
  "Signed up": noProps.describe("UNUSED: (dead code path)"),
  "Closed share modal": noProps.describe("User closed the share modal by clicking cancel OR by clicking done when sharing"),
  "Entered project list page": event({
    projectCount: number().describe("The number of projects in the project list"),
  }).describe("A project list was shown to the user"),
  "Fail to validate emails via zero bounce": event({
    response: string().describe("The error message"),
  }).describe("CONSIDER REMOVING: User's email failed to validate emails via zero bounce"),
  "Failed layer files upload": event({
    layerId: string(),
    error: string().describe("The error message"),
  }).describe("CONSIDER REMOVING: Layer files failed to upload to S3 in the layer import controller"),
  "Failed layer import": event({
    layerId: string().optional(),
    error: string(),
    completed: string().optional().nullable(),
  }).describe("Import failed in the version or layer import controller"),

  "Finished refreshing mlc": noProps.describe("APPEARS UNUSED"),

  // TODO - these Layout events could benefit from more rigorous typing
  "Layout: Added Node": event({ id: string() }),
  "Layout: Deleted Tab": event({ id: string() }),
  "Layout: Floating Window opened": event({ id: string() }),
  "Layout: Maximize/Minimize toggled": event({ id: string() }),
  "Layout: Moved Node": event({ id: string() }),
  "Layout: Reverted layout back to default": noProps,
  // TODO - this event probably shouldn't be sent at all, we capture the exception in Sentry
  "Layout: there is any issue loading the layout": event({ layout: looseObject({}) }),
  "Tour Completed": event(tourEvent).describe("A tour was completed"),
  "Tour Skipped": event(tourEvent).describe("A tour was skipped"),
  "Tour Started": event(tourEvent).describe("A tour was started"),

  // Entity actions
  "entity.starred": event({
    source: starEventSource,
    entityId,
    entityType,
  }).describe("User starred an entity"),
  "entity.unstarred": event({
    source: starEventSource,
    entityId,
    entityType,
  }).describe("User unstarred an entity"),

  "sidebar.see-all.clicked": event({
    source: oneOf(SidebarSection).describe("The section of the sidebar from which the 'See all' link was clicked"),
  }).describe("User clicked the 'See all' link in a sidebar section"),

  "sidebar.menu-item.clicked": event({
    entityId,
    entityType: entityType.optional().describe("The type of the entity that was clicked, if applicable"),
    source: oneOf(SidebarSection).describe("The section of the sidebar from which the link was clicked"),
  }).describe("User clicked an item in one of the sidebar sections"),

  "sidebar.section.expanded": event({
    source: oneOf(SidebarSection).describe("The section of the sidebar that was expanded"),
  }).describe("User expanded a section in the sidebar"),
  "sidebar.section.collapsed": event({
    source: oneOf(SidebarSection).describe("The section of the sidebar that was collapsed"),
  }).describe("User collapsed a section in the sidebar"),

  "sidebar.tips.closed": noProps.describe("User closed the sidebar tips section"),

  "sidebar.support.contact-support.clicked": noProps.describe("User clicked the 'Contact support' link in the sidebar support section"),
  "sidebar.support.contact-sales.clicked": noProps.describe("User clicked the 'Contact sales' link in the sidebar support section"),
  "sidebar.support.knowledge-base.clicked": noProps.describe("User clicked the 'Knowledge base' link in the sidebar support section"),
  "sidebar.support.provide-feedback.clicked": noProps.describe("User clicked the 'Provide feedback' link in the sidebar support section"),

  "topbar.beta-flag.share-feedback.clicked": noProps.describe("User clicked the Market Lens beta 'Share Feedback' button"),

  "homepage.recent.see-all.clicked": noProps.describe("User clicked the 'See all' button in the homepage recent items section"),
  "homepage.recent.item.clicked": event({
    entityId: string().describe("The ID of the entity that was clicked"),
    entityType: oneOf(EntityType).describe("The type of the entity that was clicked"),
  }).describe("User clicked on a recent item on the homepage"),
  "homepage.insights.users.manage.clicked": noProps.describe("User clicked on the 'Manage' button in the homepage user insights section"),
  "homepage.get-started.upgrade.clicked": noProps.describe("User clicked Upgrade Now button in the Get Started section of the homepage"),
  "homepage.get-started.plans.clicked": noProps.describe("User clicked See Plans button in the Get Started section of the homepage"),
  "homepage.get-started.buy.license.clicked": noProps.describe("User clicked Buy License button in the Get Started section of the homepage"),
  "homepage.get-started.trial.upgrade.clicked": noProps.describe("User clicked Upgrade button in the Trial Get Started section of the homepage"),
  "homepage.get-started.trial.show-info.clicked": event({
    action: oneOf(["show", "hide"]).describe("Whether the user chose to show or hide the trial info"),
  }).describe("User clicked show trial info button"),
  "homepage.plan-details.manage.clicked": noProps.describe("Clicked the Manage button from the Plan Details section on the homepage"),
  "homepage.plan-details.licenses.manage.clicked": noProps.describe(
    "Clicked the Manage licenses link on the Paid licenses row of the Plan Details section on the homepage"
  ),
  "homepage.plan-details.design.contact-sales.clicked": event({
    hasDesignPackage: boolean().describe("Whether the account already has design premises, or the row is promoting the Design package"),
  }).describe("Clicked the Talk to sales button on the Design package row of the Plan Details section on the homepage"),
  "homepage.get-started.trial.create-project.clicked": noProps.describe(
    "User clicked Create Project button in the Trial Get Started section of the homepage"
  ),
  "homepage.plan-details.trial.upgrade.clicked": noProps.describe(
    "User clicked Upgrade to paid plan button in the Plan Details section of the homepage"
  ),
  "homepage.get-started.trial.docs.link.clicked": noProps.describe(
    "User clicked Knowledge Base link in the Trial Get Started section of the homepage"
  ),
  "homepage.get-started.trial.support.link.clicked": noProps.describe(
    "User clicked Get Assistance link in the Trial Get Started section of the homepage"
  ),

  "workspace.upgrade.button.clicked": noProps.describe("User clicked Upgrade to planner button in the main workspace header"),
  "workspace.upgrade.link.clicked": noProps.describe("User clicked Upgrade link in the main workspace header"),

  "user-management.invite-members.clicked": event({
    source: oneOf(["sidebar", "user-settings"]),
  }).describe("User clicked the 'Invite Members' button"),

  "iconbar.home.clicked": noProps.describe("User clicked the 'Home' icon in the icon bar"),
  "iconbar.projects.clicked": noProps.describe("User clicked the 'Projects' icon in the icon bar"),

  "banner.trial-active.upgrade.clicked": noProps.describe("User clicked the Upgrade button in the trial active banner"),

  "banner.user-management.shown": event({
    bannerType: oneOf(BannerType).describe("The type of banner that was shown to the user"),
  }).describe("A banner was shown to the user"),

  // Account actions
  "account.pinned": event({
    accountId: string().describe("The ID of the account that was pinned"),
  }).describe("User pinned an account in the accounts list"),
  "account.unpinned": event({
    accountId: string().describe("The ID of the account that was unpinned"),
  }).describe("User unpinned an account in the accounts list"),
  "account.reordered": event({
    pinnedCount: number().describe("The number of pinned accounts after reordering"),
  }).describe("User reordered pinned accounts via drag and drop"),
};
